import {
  amber,
  green,
  grey,
  indigo,
  lightGreen,
  lime,
  orange,
  pink,
  purple,
  red,
  yellow,
} from '@material-ui/core/colors';

export function* getMemberNames<T extends Record<string, string | number>>(enumType: T): IterableIterator<string> {
  for (const enumMember in enumType) {
    if (Number.isNaN(Number(enumMember))) {
      yield enumMember;
    }
  }
}

export function getKeyByValue<T extends Record<string, string | number>>(
  enumType: T,
  value: string | number | null,
): string | null {
  if (value === null) return null;
  for (const enumMember in enumType) {
    if (Number.isNaN(Number(enumMember)) && value == enumType[enumMember]) {
      return enumMember;
    }
  }
  return null;
}

function getColorFromHex(hexColor: string): number {
  return parseInt(hexColor.replace('#', '0x'));
}

export enum QueueFilter {
  AllFiles = 'allFiles',
  OrderQueue = 'orderQueue',
}

export enum ActivityRights {
  Admin = 138,
  View = 139,
  BookOrder = 140,
}

export enum ProcessingStatus {
  New = 2920,
  'Upload Review' = 2921,
  'In Process' = 2922,
  'Review/Research' = 2923,
  'Pending Internal Response' = 2924,
  'Pending Agent Response' = 2925,
  'Pending Escrow Response' = 2926,
  Reconciled = 2927,
  Closed = 2928,
  'Returned To Queue' = 2929,
  'Pending ASC Response' = 2930,
}

export enum FileInfoStatus {
  Open = 46,
  'Opened in Error' = 47,
  Cancelled = 48,
  Closed = 49,
  'Pending Remit' = 50,
}

export enum OrderStatus {
  Open = 2950,
  Submitted = 2951,
  Closed = 2952,
  'Book Error' = 2953,
  'Bulk Upload In Process' = 2954,
  'Bulk Upload Complete' = 2955,
  Cancelled = 2956,
}

export enum AgentLinxStatus {
  None = 'None',
  Active = 'Active',
  Suspended = 'Suspended',
  PendingCancellation = 'Pending Cancellation',
  Cancelled = 'Cancelled',
  SystemCancelled = 'System Cancelled',
}

export enum RemittanceReceivablesStatus {
  Unassigned = 5,
  Assigned = 6,
  Closed = 7,
}

export enum RemittanceReceivablesCategory {
  'Payment Only' = 65,
  'Scan Only' = 66,
  Order = 67,
  Refund = 68,
  'Misc. Correspondence' = 69,
  'Missing Backup' = 70,
  'Non-FATICO' = 9206,
  'Missing Agent ID' = 9207,
  'Rescan' = 9208,
  'Inventory Requested' = 9209,
  'PFSC Clarification Required' = 9210,
  'Cash Clarification Required' = 9211,
  'Non-Agency Premium' = 9212,
  'Prepayment' = 9213,
}

export enum RemittanceReceivablesColor {
  Assigned = getColorFromHex(green[600]),
  Unassigned = getColorFromHex(orange[600]),
  Closed = getColorFromHex(grey[600]),
}

export enum ProcessingStatusColor {
  New = getColorFromHex(lightGreen[600]),
  'Upload Review' = getColorFromHex(red[600]),
  'In Process' = getColorFromHex(green[600]),
  'Review/Research' = getColorFromHex(yellow[600]),
  'Pending Internal Response' = getColorFromHex(amber[600]),
  'Pending Agent Response' = getColorFromHex(purple[600]),
  'Pending Escrow Response' = getColorFromHex(pink[600]),
  Reconciled = getColorFromHex(indigo[600]),
  Closed = getColorFromHex(grey[600]),
  'Returned To Queue' = getColorFromHex(orange[600]),
  'Pending ASC Response' = getColorFromHex(amber[600]),
}

export enum FileInfoStatusColor {
  Open = getColorFromHex(lightGreen[600]),
  'Opened in Error' = getColorFromHex(red[600]),
  Cancelled = getColorFromHex(grey[600]),
  Closed = getColorFromHex(indigo[600]),
  'Pending Remit' = getColorFromHex(yellow[600]),
}

export enum AllOrdersStatusColor {
  Open = getColorFromHex(lightGreen[600]),
  'Upload In Process' = getColorFromHex(orange[600]),
  'Upload In Progress' = getColorFromHex(orange[600]),
  'Upload Complete' = getColorFromHex(lime[600]),
  Closed = getColorFromHex(grey[600]),
  Submitted = getColorFromHex(indigo[600]),
}

export const defaultFscTeam = {
  id: 0,
  team: '',
  role: 'FSC OM',
};

export enum TeamAssignment {
  IFSC = 3001,
  PFSC = 3002,
  CRS = 3003,
  //CIU = 3004,
}

// handling numeric enum iterating
export function getTeamAssignmentIds(): number[] {
  const teamAssignmentIds: number[] = [];
  for (const value of Object.values(TeamAssignment)) {
    if (typeof value === 'number') {
      teamAssignmentIds.push(value);
    }
  }
  return teamAssignmentIds;
}

export enum ReasonCode {
  'QA' = 2970,
  'Missing Inventory' = 2971,
  'Inventory Issue' = 2972,
  'Missing File Info' = 2973,
  'OAM' = 2974,
  'Multi-issue' = 2976,
  'KY Tax' = 2977,
  'Co-Insurance' = 2978,
  'Premium Issue' = 2979,
  'ASC Support Required' = 2980,
  'Shared Services' = 2981,
}

export enum ApiStatus {
  Idle = 'idle',
  Pending = 'pending',
  Success = 'success',
  Error = 'error',
}

export function getColorsByProcessingStatus(): { [status: string]: string } {
  const colors: { [status: string]: string } = {};
  const processingStatusColors = getMemberNames(ProcessingStatusColor);
  let processingStatusColor = processingStatusColors.next();
  while (!processingStatusColor.done) {
    const colorCode = ProcessingStatusColor[processingStatusColor.value as keyof typeof ProcessingStatusColor];
    colors[processingStatusColor.value] = '#' + colorCode.toString(16).padStart(6, '0');
    processingStatusColor = processingStatusColors.next();
  }
  return colors;
}

export function getColorsByFileInfoStatus(): { [status: string]: string } {
  const colors: { [status: string]: string } = {};
  const fileInfoStatusColors = getMemberNames(FileInfoStatusColor);
  let fileInfoStatusColor = fileInfoStatusColors.next();
  while (!fileInfoStatusColor.done) {
    const colorCode = FileInfoStatusColor[fileInfoStatusColor.value as keyof typeof FileInfoStatusColor];
    colors[fileInfoStatusColor.value] = '#' + colorCode.toString(16).padStart(6, '0');
    fileInfoStatusColor = fileInfoStatusColors.next();
  }
  return colors;
}

export const RESTRICTED_QUEUE_STATUS_IDS: readonly number[] = [
  FileInfoStatus.Cancelled,
  FileInfoStatus['Opened in Error'],
  FileInfoStatus.Closed,
];

export const isFileQueueable = (file: { fileInfoStatusId?: number }): boolean => {
  if (!file.fileInfoStatusId) return false; // Do not queue if status unknown.
  return !RESTRICTED_QUEUE_STATUS_IDS.includes(file.fileInfoStatusId);
};

export const getRestrictedFiles = <T extends { fileInfoStatusId?: number }>(files: T[]): T[] => {
  return files.filter((file) => !isFileQueueable(file));
};
